/* eslint-disable-next-line line-comment-position */
import type { AxiosRequestConfig, AxiosResponse, ResponseType as AxiosResponseType } from 'axios';
import axios from 'axios';
import type { FETCH_TYPES, IFetchRequest, IFetchResponse, ResponseTypes } from '../interface/fetch';
import type { IProgressPromise, IProgressPromiseGenerator } from '../interface/promise';
import arrayBuffer from '../util/arrayBuffer';
import buffer from '../util/buffer';
import mathUtils from '../util/math';
import fetchUtils from './fetch.util';

/* Make K a required prop of T */
type WithRequired<T, K extends keyof T> = T & { [P in K]-?: T[P] };
const DEFAULT_TIMEOUT_MS = 30_000; /* 30s */
const isRunningOnNodeJS = typeof window === 'undefined';

type ResponseBuilder = (response: Response) => Promise<unknown | undefined>;

const mapResponse: Record<ResponseTypes, ResponseBuilder> = {
    json: response => response.json(),
    text: response => response.text(),
    blob: response => response.blob(),
    arraybuffer: response => response.arrayBuffer(),
    void: response => Promise.resolve(undefined),
    opaque: response => response.blob(),
};

const TypeMap: Record<AxiosResponseType, ResponseTypes> = {
    arraybuffer: 'arraybuffer',
    blob: 'blob',
    document: 'text',
    json: 'json',
    text: 'text',
    stream: 'blob',
};

async function _fetchAdapter<T>(config: AxiosRequestConfig<T>): Promise<AxiosResponse<T>> {
    /* Typed as any from Axios */
    /* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */
    const parameters: RequestInit = {
        headers: config.headers ?? {},
        ...config.params,
    };
    const result = await fetch(config.url ?? '', parameters);

    const responseHeaders: Record<string, string> = {};

    /* #forEach exists as defined function on object type 'Headers'. Ignore eslint */
    /* eslint-disable-next-line unicorn/no-array-for-each */
    result.headers.forEach((value, key) => (responseHeaders[key] = value));

    const mapper: ResponseBuilder = mapResponse[TypeMap[config.responseType ?? 'blob']];

    const response: AxiosResponse<T> = {
        data: (await mapper(result)) as T,
        status: result.status,
        statusText: result.statusText,
        headers: responseHeaders,
        config,
    };

    return response;
}

async function _axiosFetch<T extends FETCH_TYPES>(
    parameters: AxiosRequestConfig,
    requestPayload: IFetchRequest,
    progressCallback: (progress: number) => void,
): Promise<AxiosResponse<T, unknown>> {
    parameters.onDownloadProgress = (progressEvent: ProgressEvent) => {
        const pc = mathUtils.clamp(progressEvent.loaded / progressEvent.total, 0, 1);
        progressCallback(pc);
    };

    const response = await axios.request<T>(parameters);

    return response;
}

/* eslint-disable-next-line @typescript-eslint/promise-function-async */
function fetcher<T extends FETCH_TYPES>(
    promiser: IProgressPromiseGenerator,
    requestPayload: IFetchRequest,
    timeout = DEFAULT_TIMEOUT_MS,
): IProgressPromise<IFetchResponse<T>> {
    const parameters: WithRequired<AxiosRequestConfig, 'headers'> = {
        method: requestPayload.method ?? 'GET',
        url: requestPayload.url,
        headers: {},
        data: undefined,
        params: {},
    };

    for (const element of requestPayload.headers ?? []) {
        parameters.headers[element.name] = element.value;
    }

    if (
        requestPayload.expectedResponseType &&
        requestPayload.expectedResponseType !== 'opaque' &&
        requestPayload.expectedResponseType !== 'void'
    ) {
        parameters.responseType = requestPayload.expectedResponseType;
    }

    if (requestPayload.body !== undefined) {
        parameters.data = requestPayload.body;
    }

    parameters.timeout = timeout;

    const expectOpaqueResponse =
        requestPayload.expectedResponseType === 'opaque' &&
        !isRunningOnNodeJS; /* No FetchJS on NodeJS (v16) */

    if (expectOpaqueResponse) {
        Object.assign(parameters.params, { mode: 'no-cors' });
        parameters.adapter =
            _fetchAdapter; /* Mode=no-cors only supported on Fetch requests *not* XHR */
    }

    const task = promiser.async<IFetchResponse<T>>(async progressCallback => {
        const response = await _axiosFetch<T>(parameters, requestPayload, (newProgress: number) => {
            progressCallback(newProgress);
        });

        let payload: T | null = null;

        if (expectOpaqueResponse) {
            /* Cannot read opaque responses, set as null */
        } else if (parameters.responseType === 'arraybuffer' && buffer.isBuffer(response.data)) {
            /* Axios will not convert Buffer to ArrayBuffer, even when asked for one, explicitly convert */
            payload = arrayBuffer.bufferToArrayBuffer(response.data) as T;
        } else {
            payload = response.data;
        }

        const send: IFetchResponse<T> = {
            request: requestPayload,
            payload,
            statusCode: response.status,
            isStatusOk: fetchUtils.isStatusOK(response.status),
        };

        return send;
    });

    return task;
}

export default fetcher;
